import { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, useUserId, useSessionCheck } from '@talktoast/shared'
import { usePreferences } from '@talktoast/shared'
import { useShoppingList } from '@talktoast/shared'
import { backendApiService } from '@talktoast/shared'
import { recipeApiService } from '@talktoast/shared'
import { sessionApiService } from '@talktoast/shared'
import { favoritesApiService } from '@talktoast/shared'
import InitialSetup from './preferences/InitialSetup'
import QuickStart from './preferences/QuickStart'
import SessionPreferences from './preferences/SessionPreferences'
import SettingsHub from './preferences/SettingsHub'
import MealPlanningChat from './MealPlanningChat'
import ShoppingListScreen from './ShoppingListScreen'
import ShoppingActionsScreen from './ShoppingActionsScreen'
import CalendarScreenV2 from './calendar/CalendarScreenV2'
import RecipeHistoryScreen from './RecipeHistoryScreen'
import TodayPage from '../pages/TodayPage'
import PreferencesPage from '../pages/PreferencesPage'
import { SubscriptionPricingView, SubscriptionManagementView, CheckoutModal } from './subscription'
import { parseRecipeResponse, isRecipeResponse } from '@talktoast/shared'
import { SyncStatus } from '@talktoast/shared'
import { SessionPreferences as SessionPrefs } from '@talktoast/shared'
import { Button, Card, Badge, Modal, Container } from '@talktoast/ui'
import { ToastyLoading } from '@talktoast/shared'
import { showError, showWarning } from '../utils/toast'
import { clientLogger } from '@talktoast/shared'
import Layout from './Layout'

interface HomeProps {
  onHeaderNavigate?: (path: string) => void
}

const Home = forwardRef((props: HomeProps, ref) => {
  // CRITICAL: ALL useState hooks MUST be declared BEFORE any other hooks (useImperativeHandle, useEffect, etc.)
  // This is a React rule - hooks must be called in the same order every render

  // View state
  const [currentView, setCurrentView] = useState<'home' | 'setup' | 'session' | 'planning' | 'shopping' | 'shopping-actions' | 'calendar' | 'recipe-history' | 'favorites' | 'settings' | 'settings-dietary' | 'settings-appliances' | 'settings-diet-preference' | 'settings-session' | 'preferences' | 'subscription-pricing' | 'subscription-management'>('home')
  const [previousView, setPreviousView] = useState<string>('home')

  // Session state
  const [currentSessionPrefs, setCurrentSessionPrefs] = useState<SessionPrefs | null>(null)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ state: 'idle', lastSync: null, error: null })
  const [currentRecipes, setCurrentRecipes] = useState<any[]>([])
  const [currentSessionId, setCurrentSessionId] = useState<string>('')
  const [existingMealSession, setExistingMealSession] = useState<any>(null)
  const [showSessionPrompt, setShowSessionPrompt] = useState(false)
  const [isGeneratingShoppingList, setIsGeneratingShoppingList] = useState(false)
  const [isResuming, setIsResuming] = useState(false)
  const [unassignedMealCount, setUnassignedMealCount] = useState<number>(0)

  // Checkout modal state
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false)
  const [checkoutTier, setCheckoutTier] = useState<'premium'>('premium')
  const [checkoutInterval, setCheckoutInterval] = useState<'monthly' | 'yearly'>('yearly')

  // Flag set when user enters planning via "Plan Your Week" — forces 5-day workweek defaults
  const [forceWeeklyDefaults, setForceWeeklyDefaults] = useState(false)

  const navigate = useNavigate()

  // Initialize all custom hooks AFTER useState but BEFORE useImperativeHandle
  const { user, profile, isAuthenticated, isGuest, guestSession, signOut, createGuestSession, loading: authLoading } = useAuth()
  const userId = useUserId() // Custom UUID architecture - works for both guest and authenticated users
  const {
    needsInitialSetup,
    hasRememberedSessionPrefs,
    updatePersistentPreferences,
    updateSessionPreferences,
    getSessionPreferences,
    getPersistentPreferences,
    loading: prefsLoading
  } = usePreferences()
  const { generateShoppingList } = useShoppingList()
  const { checkForActiveSession } = useSessionCheck()

  // Expose setView and resumeSession methods to parent via ref
  // Must come AFTER all hooks it depends on
  useImperativeHandle(ref, () => ({
    setView: (view: string) => {
      setCurrentView(view as any)
    },
    resumeSession: (sessionId: string, status: string) => {
      // Directly resume the session without showing the modal
      const session = { id: sessionId, status: status as any }
      setCurrentSessionId(sessionId)
      setIsResuming(true)
      console.log('[Home] Resuming session via ref:', sessionId, status)

      // Navigate to appropriate view based on session status
      switch (status) {
        case 'created':
        case 'planning': {
          const savedPrefs = getSessionPreferences()
          setCurrentSessionPrefs(savedPrefs)
          setCurrentView('planning')
          break
        }
        case 'shopping':
          setCurrentView('shopping')
          break
        case 'scheduling':
          setCurrentView('calendar')
          break
        default:
          setCurrentView('session')
      }
    }
  }), [getSessionPreferences])


  const handleStartPlanning = async () => {
    if (authLoading || !userId) {
      showError('Please wait a moment while we load your profile, then try again.')
      return
    }

    if (needsInitialSetup()) {
      setCurrentView('setup')
      return
    }

    // Use consolidated session check hook
    const result = await checkForActiveSession()

    if (result.hasSession) {
      // Found existing session - prompt user
      setExistingMealSession(result.session)
      setUnassignedMealCount(result.unassignedCount)
      setShowSessionPrompt(true)
      console.log('[Home] Found existing meal session:', result.session.status)
      return
    }

    // No existing session found - proceed with new session
    proceedWithNewSession()
  }

  const proceedWithNewSession = async () => {
    // Clear any old state, including the stored session ID so there is no window
    // where a stale ID could be picked up if this function suspends mid-flight.
    setCurrentRecipes([])
    setCurrentSessionId('')
    setExistingMealSession(null)
    sessionApiService.clearActiveSessionId()

    if (hasRememberedSessionPrefs()) {
      // Ensure userId is available before proceeding
      if (!userId) {
        console.error('[Home] Cannot create meal session - userId is null (profile may still be loading)')
        // Caller (handleStartPlanning) already showed the error toast — return silently
        return
      }

      // Load saved preferences and go straight to planning
      const savedPrefs = getSessionPreferences()
      setCurrentSessionPrefs(savedPrefs)
      console.log('[Home] Using remembered session preferences:', savedPrefs)

      // Create meal session BEFORE going to planning.
      // Backend abandons any lingering active session and creates a fresh one.
      try {
        const persistentPrefs = getPersistentPreferences()

        console.log('[Home] Creating meal session for remembered preferences', { userId, isGuest, isAuthenticated })
        const result = await backendApiService.createOrGetMealSession(
          isGuest ? null : userId, // userId for authenticated users only
          isGuest ? userId : null, // guestSessionId for guests only
          savedPrefs,
          persistentPrefs
        )

        setCurrentSessionId(result.session.id)
        sessionApiService.setActiveSessionId(result.session.id)
        console.log('[Home] Meal session created:', result.session.id)

        // IMPORTANT: Only navigate to planning AFTER session is created and state is updated
        // Wait for React state update to complete before navigating
        await new Promise(resolve => setTimeout(resolve, 0))
        setCurrentView('planning')
      } catch (error) {
        console.error('[Home] Failed to create meal session:', error)
        // Don't block the user - they can still try to proceed
        showWarning('Failed to create meal session. Your recipes may not be saved.')
        // Still allow them to proceed even if session creation failed
        setCurrentView('planning')
      }
    } else {
      // Ask for session preferences
      setCurrentView('session')
    }
  }

  const handleContinueExistingSession = () => {
    setShowSessionPrompt(false)
    if (!existingMealSession) return

    setCurrentSessionId(existingMealSession.id)
    setIsResuming(true) // Flag that we're resuming an existing session
    console.log('[Home] Continuing existing session:', existingMealSession.id)

    // Resume based on session status
    switch (existingMealSession.status) {
      case 'created':
      case 'planning': {
        // In planning phase
        const savedPrefs = getSessionPreferences()
        setCurrentSessionPrefs(savedPrefs)
        setCurrentView('planning')
        break
      }
      case 'shopping':
        setCurrentView('shopping')
        break
      case 'scheduling':
        setCurrentView('calendar')
        break
      default:
        proceedWithNewSession()
    }
  }

  const handleStartFreshSession = async () => {
    setShowSessionPrompt(false)
    setIsResuming(false) // Starting fresh, not resuming

    // Abandon the old session and purge its recipes before starting fresh.
    // Purging session_recipes prevents stale data from surfacing if something
    // goes wrong with session ID routing later.
    if (existingMealSession && userId) {
      try {
        console.log('[Home] Abandoning old session:', existingMealSession.id)
        await backendApiService.clearSessionRecipes(existingMealSession.id)
        await backendApiService.updateMealSessionStatus(existingMealSession.id, 'complete')
        console.log('[Home] Old session abandoned and recipes cleared')
      } catch (error) {
        console.error('[Home] Error abandoning old session:', error)
        // Continue anyway - don't block the user if API call fails
      }
    }

    proceedWithNewSession()
  }

  const handleSetupComplete = async (persistentPrefs: any) => {
    await updatePersistentPreferences(persistentPrefs)
    setCurrentView('session')
  }

  const handleSkipSetup = async () => {
    // Use smart defaults without saving preferences
    // This allows users to get to value immediately
    setCurrentView('session')
  }

  const handleSettingsNavigate = (destination: 'dietary' | 'appliances' | 'diet-preference' | 'session-prefs' | 'subscription-management') => {
    if (destination === 'subscription-management') {
      setPreviousView('settings')
      setCurrentView('subscription-management')
    } else {
      setCurrentView(`settings-${destination}` as any)
    }
  }

  const handleSettingsSave = async (persistentPrefs: any) => {
    await updatePersistentPreferences(persistentPrefs)
    setCurrentView('settings')
  }


  // Check for active session on mount
  useEffect(() => {
    const checkForActiveSession = async () => {
      try {
        const storedSessionId = sessionApiService.getActiveSessionId()
        if (!storedSessionId) {
          console.log('[Home] No stored session ID found')
          return
        }

        if (!userId) {
          console.log('[Home] No user ID available for session recovery')
          return
        }

        console.log(`[Home] Checking for active session: ${storedSessionId}`)
        const activeSession = await sessionApiService.getActiveSession(userId)

        if (activeSession && activeSession.session_id === storedSessionId) {
          console.log('[Home] Found active session:', activeSession.status)

          // Restore session state based on status
          setCurrentSessionId(activeSession.session_id)

          // Load recipes if available
          if (activeSession.current_meals && activeSession.current_meals.length > 0) {
            setCurrentRecipes(activeSession.current_meals)
            console.log(`[Home] Restored ${activeSession.current_meals.length} meals from session`)
          }

          // Navigate to appropriate view based on status
          switch (activeSession.status) {
            case 'planning':
              // User was in the middle of planning - could show resume modal
              console.log('[Home] Session was in planning state')
              break
            case 'generated':
              // Meals were generated but not yet in shopping
              console.log('[Home] Session has generated meals')
              break
            case 'shopping':
              setCurrentView('shopping')
              console.log('[Home] Resuming shopping view')
              break
            case 'scheduling':
              setCurrentView('calendar')
              console.log('[Home] Resuming calendar view')
              break
            default:
              console.log(`[Home] Session in ${activeSession.status} state`)
          }
        } else {
          console.log('[Home] No matching active session found, clearing stored ID')
          sessionApiService.clearActiveSessionId()
        }
      } catch (error) {
        console.error('[Home] Error checking for active session:', error)
      }
    }

    checkForActiveSession()
  }, [userId])

  // Helper to navigate with history tracking
  const navigateToView = useCallback((newView: string) => {
    setPreviousView(currentView)
    setCurrentView(newView)
  }, [currentView])

  const handleSessionPrefsUpdate = useCallback(async (sessionPrefs: SessionPrefs) => {
    console.log('[Home] Session preferences updated, saving:', sessionPrefs)
    // Update local state immediately
    setCurrentSessionPrefs(sessionPrefs)

    // Save to persistent storage for use during meal planning
    try {
      await updateSessionPreferences(sessionPrefs)
      console.log('[Home] Session preferences saved successfully')
    } catch (error) {
      console.error('[Home] Failed to save session preferences:', error)
      // Note: The SessionPreferences component will handle the error display
    }
  }, [updateSessionPreferences])

  const handleSyncStatusChange = useCallback((status: SyncStatus) => {
    console.log('[Home] Sync status changed:', status)
    setSyncStatus(status)
  }, [])

  const handleStartMealPlanning = useCallback(async () => {
    // Wait for any pending sync operations before starting meal planning
    console.log('[Home] Starting meal planning - checking sync status:', syncStatus)

    if (syncStatus.state === 'syncing') {
      console.log('[Home] Waiting for preference sync to complete...')
      // The SessionPreferences component should prevent this, but just in case
      return
    }

    if (syncStatus.state === 'failed') {
      console.log('[Home] Cannot start meal planning - preference sync failed')
      return
    }

    // Ensure userId is available before proceeding
    if (authLoading || !userId) {
      console.error('[Home] Cannot start planning - userId is null (profile may still be loading)')
      showError('Please wait a moment while we load your profile, then try again.')
      return
    }

    // Create meal session BEFORE starting planning.
    // Backend abandons any lingering active session and creates a fresh one.
    try {
      const persistentPrefs = getPersistentPreferences()

      console.log('[Home] Creating meal session before starting planning', { userId, isGuest, isAuthenticated })
      const result = await backendApiService.createOrGetMealSession(
        isGuest ? null : userId, // userId for authenticated users only
        isGuest ? userId : null, // guestSessionId for guests only
        currentSessionPrefs,
        persistentPrefs
      )

      setCurrentSessionId(result.session.id)
      sessionApiService.setActiveSessionId(result.session.id)
      console.log('[Home] Meal session created:', result.session.id)

      // IMPORTANT: Only navigate to planning AFTER session is created and state is updated
      // Wait for React state update to complete before navigating
      await new Promise(resolve => setTimeout(resolve, 0))
      console.log('[Home] Starting meal planning with preferences:', currentSessionPrefs)
      setCurrentView('planning')
    } catch (error) {
      console.error('[Home] Failed to create meal session:', error)
      // Don't block the user - they can still try to proceed
      showWarning('Failed to create meal session. Your recipes may not be saved.')
      // Still allow them to proceed even if session creation failed
      setCurrentView('planning')
    }
  }, [currentSessionPrefs, syncStatus, userId, authLoading, getPersistentPreferences])

  const handleShowAuth = useCallback(async (authType: 'login' | 'signup') => {
    if (authType === 'login') {
      // For now, create guest session since we don't have auth UI implementation yet
      await createGuestSession()
    } else if (authType === 'signup') {
      // For now, create guest session since we don't have auth UI implementation yet
      await createGuestSession()
    }
  }, [createGuestSession])

  // Save recipes to local storage for history viewing
  const saveRecipesToHistory = useCallback(async (recipes: any[]) => {
    try {
      const storageKey = `recipe_history_${userId || 'anonymous'}`

      // Load existing history
      const existingHistory = localStorage.getItem(storageKey)
      const history = existingHistory ? JSON.parse(existingHistory) : []

      // Add new recipes with current date
      const newRecipes = recipes.map(recipe => ({
        id: recipe.id,
        name: recipe.name,
        description: recipe.description,
        mealType: recipe.tags?.[0] || 'dinner', // Use first tag as meal type
        prepTime: recipe.prepTime,
        cookTime: recipe.cookTime,
        servings: recipe.servings,
        rating: null,
        dateUsed: new Date().toISOString()
      }))

      // Combine and deduplicate (keep latest entry for same recipe)
      const updatedHistory = [...history]
      newRecipes.forEach(newRecipe => {
        const existingIndex = updatedHistory.findIndex(r => r.name === newRecipe.name)
        if (existingIndex >= 0) {
          updatedHistory[existingIndex] = newRecipe
        } else {
          updatedHistory.push(newRecipe)
        }
      })

      // Sort by date used (newest first)
      updatedHistory.sort((a, b) => new Date(b.dateUsed).getTime() - new Date(a.dateUsed).getTime())

      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(updatedHistory))

      console.log('[Home] Saved recipes to history:', updatedHistory.length)
    } catch (error) {
      console.error('[Home] Failed to save recipes to history:', error)
    }
  }, [user, guestSession])

  const handleMealPlanComplete = useCallback(async (recipeDetails: Map<string, any>, sessionId: string) => {
    setIsGeneratingShoppingList(true)

    try {
      console.log('[Home] Meal plan completed, generating shopping list')
      console.log('[Home] Session ID:', sessionId)
      console.log('[Home] Available recipe details:', Array.from(recipeDetails.keys()))

      // Store sessionId for calendar view and in localStorage
      setCurrentSessionId(sessionId)
      sessionApiService.setActiveSessionId(sessionId)

      let fullRecipes = []

      // First, try to use the recipes from the passed recipeDetails Map
      if (recipeDetails.size > 0) {
        console.log(`[Home] Using recipes from in-memory recipeDetails - found ${recipeDetails.size} recipes`)

        for (const [recipeName, recipeDetailResponse] of recipeDetails.entries()) {
          console.log(`[Home] Processing recipe: ${recipeName}`)
          console.log(`[Home] Ingredients for ${recipeName}:`, recipeDetailResponse.recipe.ingredients)

          fullRecipes.push({
            id: `recipe_${Date.now()}_${Math.random()}`,
            name: recipeDetailResponse.recipe.name,
            description: recipeDetailResponse.recipe.description || 'Delicious recipe',
            ingredients: recipeDetailResponse.recipe.ingredients,
            instructions: recipeDetailResponse.recipe.instructions,
            prepTime: recipeDetailResponse.recipe.prepTime,
            cookTime: recipeDetailResponse.recipe.cookTime,
            servings: recipeDetailResponse.recipe.servings,
            nutrition: recipeDetailResponse.recipe.nutrition,
            tags: recipeDetailResponse.recipe.tags || []
          })
        }
      } else {
        // Fallback: try to load recipes from database using the provided sessionId
        console.log('[Home] No in-memory recipes found, attempting to load from database')

        try {
          if (sessionId) {
            console.log('[Home] Using provided sessionId to load recipes:', sessionId)
            const recipes = await backendApiService.getSessionRecipes(sessionId)
            console.log(`[Home] Loaded ${recipes.length} recipes from database:`, recipes.map(r => r.recipe_name || r.name))

            fullRecipes = recipes.map(recipe => ({
              id: recipe.id,
              name: recipe.recipe_name || recipe.name,
              description: recipe.recipe_data?.description || recipe.description || 'Delicious recipe',
              ingredients: recipe.recipe_data?.ingredients || recipe.ingredients || [],
              instructions: recipe.recipe_data?.instructions || recipe.instructions || [],
              prepTime: recipe.recipe_data?.prepTime || recipe.prepTime || 30,
              cookTime: recipe.recipe_data?.cookTime || recipe.cookTime || 30,
              servings: recipe.recipe_data?.servings || recipe.servings || 4,
              nutrition: recipe.recipe_data?.nutrition || recipe.nutrition || null,
              tags: recipe.recipe_data?.tags || recipe.tags || []
            }))
            console.log(`[Home] Processed ${fullRecipes.length} recipes for shopping list`)
          } else {
            console.error('[Home] No sessionId available to load recipes from database')
          }
        } catch (dbError) {
          console.warn('[Home] Failed to load recipes from database:', dbError)
        }

        // If database loading failed, try parsing from messages as final fallback
        if (fullRecipes.length === 0) {
          console.log('[Home] Database loading failed, attempting to parse from messages')

          const recipeMessage = messages
            .slice()
            .reverse()
            .find(msg => msg.role === 'assistant' && isRecipeResponse(msg.content))

          if (recipeMessage) {
            const parsedRecipes = parseRecipeResponse(recipeMessage.content)
            console.log('[Home] Parsed recipe names from messages:', parsedRecipes.map(r => r.name))

            fullRecipes = parsedRecipes.map(parsedRecipe => ({
              id: parsedRecipe.id,
              name: parsedRecipe.name,
              description: `${parsedRecipe.mealType} recipe`,
              ingredients: [
                { name: 'ingredients not loaded', amount: 1, unit: 'item', category: 'other' }
              ],
              instructions: [`Prepare ${parsedRecipe.name}`],
              prepTime: 30,
              cookTime: 30,
              servings: 4,
              nutrition: null,
              tags: [parsedRecipe.mealType]
            }))
          }
        }
      }

      console.log(`[Home] Final recipe count: ${fullRecipes.length}`)
      console.log('[Home] Full recipes with ingredients:', fullRecipes.map(r => `${r.name} (${r.ingredients?.length || 0} ingredients)`))

      if (fullRecipes.length === 0) {
        console.warn('[Home] No full recipes available for shopping list generation')
        return
      }

      // Store recipes for calendar view
      setCurrentRecipes(fullRecipes)

      // Save recipes to favorites table for history/rating
      // Note: Recipes stay in session_recipes table and will be promoted to recipes table
      // when user transitions from shopping to scheduling phase
      try {
        if (userId && sessionId) {
          // Save to favorites table with default rating (no thumbs, not favorited yet)
          // This allows users to rate/favorite recipes later in Recipe History
          console.log(`[Home] Saving ${fullRecipes.length} recipes to favorites table for future rating`)
          for (const recipe of fullRecipes) {
            try {
              await favoritesApiService.updateRecipeRating(userId, {
                recipeHash: favoritesApiService.generateRecipeHash(recipe.name),
                recipeName: recipe.name,
                sessionId,
                thumbsRating: 0, // No rating initially
                isFavorite: false // Not favorited initially
              })
            } catch (favError) {
              // Recipe might already exist in favorites, ignore duplicate errors
              console.warn(`[Home] Could not save recipe ${recipe.name} to favorites (may already exist):`, favError)
            }
          }
          console.log('[Home] Recipes saved to favorites table for future rating')
        } else {
          console.warn('[Home] Cannot save recipes: missing userId or sessionId')
        }
      } catch (saveError) {
        console.error('[Home] Failed to save recipes to database:', saveError)
        // Continue anyway - don't block the user flow
      }

      // Generate shopping list with real ingredient data
      await generateShoppingList(fullRecipes)

      // Update meal plan session status to 'shopping'
      try {
        await backendApiService.updateMealSessionStatus(sessionId, 'shopping')
        console.log('[Home] Updated meal session status to shopping')
      } catch (statusError) {
        console.error('[Home] Failed to update meal session status:', statusError)
        // Continue anyway - don't block the user flow
      }

      // Navigate to shopping list view
      setCurrentView('shopping')

      console.log('[Home] Successfully generated shopping list')
    } catch (error) {
      console.error('[Home] Error generating shopping list:', error)
      // Could show an error toast here
    } finally {
      setIsGeneratingShoppingList(false)
    }
  }, [generateShoppingList, navigate])

  if (prefsLoading) {
    return (
      <Container className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ToastyLoading className="mx-auto mb-3 w-16 h-16" />
          <p className="text-gray-600 font-medium">Loading your preferences...</p>
        </div>
      </Container>
    )
  }

  // Show settings hub
  if (currentView === 'settings') {
    return (
      <SettingsHub
        onNavigate={handleSettingsNavigate}
        onBack={() => setCurrentView('home')}
      />
    )
  }

  // Show individual settings screens
  if (currentView === 'settings-dietary') {
    return (
      <InitialSetup
        startStep={2}
        singleStepMode={true}
        initialPreferences={getPersistentPreferences()}
        fromSettings={true}
        onBack={() => setCurrentView('settings')}
        onComplete={handleSettingsSave}
      />
    )
  }

  if (currentView === 'settings-appliances') {
    return (
      <InitialSetup
        startStep={4}
        singleStepMode={true}
        initialPreferences={getPersistentPreferences()}
        fromSettings={true}
        onBack={() => setCurrentView('settings')}
        onComplete={handleSettingsSave}
      />
    )
  }

  if (currentView === 'settings-diet-preference') {
    return (
      <InitialSetup
        startStep={1}
        singleStepMode={true}
        initialPreferences={getPersistentPreferences()}
        fromSettings={true}
        onBack={() => setCurrentView('settings')}
        onComplete={handleSettingsSave}
      />
    )
  }

  if (currentView === 'settings-session-prefs') {
    return (
      <SessionPreferences
        initialPreferences={hasRememberedSessionPrefs() ? getSessionPreferences() : null}
        onUpdate={handleSessionPrefsUpdate}
        onSyncStatusChange={handleSyncStatusChange}
        onStart={() => setCurrentView('settings')}
        fromSettings={true}
      />
    )
  }

  // Show setup flow if needed
  if (currentView === 'setup') {
    return (
      <QuickStart
        onComplete={handleSetupComplete}
        onSkip={handleSkipSetup}
        onShowAuth={handleShowAuth}
        isGuest={isGuest}
      />
    )
  }

  // Show session preferences
  if (currentView === 'session') {
    return (
      <SessionPreferences
        initialPreferences={hasRememberedSessionPrefs() ? getSessionPreferences() : null}
        forceWeeklyDefaults={forceWeeklyDefaults}
        onUpdate={handleSessionPrefsUpdate}
        onSyncStatusChange={handleSyncStatusChange}
        onStart={() => {
          // Clear the flag once the user has seen and confirmed the weekly defaults
          setForceWeeklyDefaults(false)
          handleStartMealPlanning()
        }}
      />
    )
  }

  // Show meal planning chat
  if (currentView === 'planning') {
    return (
      <>
        <MealPlanningChat
          sessionPreferences={currentSessionPrefs}
          initialMealSessionId={currentSessionId}
          isResuming={isResuming}
          onBack={() => setCurrentView('home')}
          onContinue={(recipeDetails, sessionId) => handleMealPlanComplete(recipeDetails, sessionId)}
          onNavigateToSubscription={() => setCurrentView('subscription-management')}
        />

        {/* Loading overlay while generating shopping list */}
        {isGeneratingShoppingList && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="text-center max-w-md px-6">
              <ToastyLoading className="mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-2">Preparing Your Shopping List</h3>
              <p className="text-gray-200 mb-4">
                Creating your personalized grocery list...
              </p>
            </div>
          </div>
        )}
      </>
    )
  }

  // Show shopping list
  if (currentView === 'shopping') {
    return (
      <ShoppingListScreen
        onBack={() => setCurrentView('home')}
        onNext={async () => {
          // Promote recipes from session_recipes to recipes table
          if (currentSessionId) {
            try {
              if (userId) {
                console.log('[Home] Promoting recipes from session_recipes to recipes table')
                await backendApiService.promoteSessionRecipes(userId, currentSessionId)
                console.log('[Home] Recipes promoted successfully')
              }
            } catch (error) {
              console.error('[Home] Failed to promote recipes:', error)
              // Continue anyway - don't block user flow
            }
          }
          setCurrentView('shopping-actions')
        }}
      />
    )
  }

  // Show shopping actions
  if (currentView === 'shopping-actions') {
    return (
      <ShoppingActionsScreen
        onBack={() => setCurrentView('shopping')}
        onContinueToCalendar={async () => {
          // Update meal plan session status to 'scheduling'
          if (currentSessionId) {
            try {
              await backendApiService.updateMealSessionStatus(currentSessionId, 'scheduling')
              console.log('[Home] Updated meal session status to scheduling')
            } catch (error) {
              console.error('[Home] Failed to update meal session status:', error)
            }
          }
          navigateToView('calendar')
        }}
      />
    )
  }

  // Show calendar
  if (currentView === 'calendar') {
    return (
      <CalendarScreenV2
        onBack={() => setCurrentView('home')}
        sessionId={currentSessionId}
        onNavigateToSubscription={() => setCurrentView('subscription-management')}
      />
    )
  }

  // Show recipe history
  if (currentView === 'recipe-history') {
    return (
      <RecipeHistoryScreen
        onBack={() => setCurrentView('home')}
      />
    )
  }

  // Show favorites (recipe history with favorites filter)
  if (currentView === 'favorites') {
    return (
      <RecipeHistoryScreen
        onBack={() => setCurrentView('home')}
        initialFilter="favorites"
      />
    )
  }

  // Show preferences page
  if (currentView === 'preferences') {
    return (
      <PreferencesPage
        onBack={() => setCurrentView('home')}
      />
    )
  }

  // Show subscription pricing view
  if (currentView === 'subscription-pricing') {
    return (
      <>
        <SubscriptionPricingView
          onBack={() => setCurrentView(previousView as any)}
          onSelectTier={(tier, interval) => {
            setCheckoutTier(tier)
            setCheckoutInterval(interval)
            setCheckoutModalOpen(true)
          }}
          currentTier={subscription?.tier}
        />
        <CheckoutModal
          isOpen={checkoutModalOpen}
          onClose={() => setCheckoutModalOpen(false)}
          tier={checkoutTier}
          defaultInterval={checkoutInterval}
        />
      </>
    )
  }

  // Show subscription management view
  if (currentView === 'subscription-management') {
    return (
      <>
        <SubscriptionManagementView
          onBack={() => setCurrentView('settings')}
          onUpgrade={() => {
            setPreviousView(currentView)
            setCurrentView('subscription-pricing')
          }}
        />
        <CheckoutModal
          isOpen={checkoutModalOpen}
          onClose={() => setCheckoutModalOpen(false)}
          tier={checkoutTier}
          defaultInterval={checkoutInterval}
        />
      </>
    )
  }

  // Default home view - Today Page
  return (
    <>
      {/* Session Resumption Modal */}
      {showSessionPrompt && existingMealSession && (
        <Modal
          open={showSessionPrompt}
          onClose={handleStartFreshSession}
          title={existingMealSession.status === 'scheduling' && unassignedMealCount > 0 ? 'You have an incomplete meal plan' : 'Continue Your Meal Plan?'}
        >
          <p className="text-gray-600 mb-4">
            {existingMealSession.status === 'scheduling' && unassignedMealCount > 0 ? (
              <>
                You have <strong>{unassignedMealCount} unscheduled {unassignedMealCount === 1 ? 'meal' : 'meals'}</strong> from your previous planning session.
              </>
            ) : (
              'You have an in-progress meal planning session. Would you like to continue where you left off or start fresh?'
            )}
          </p>

          {existingMealSession.status === 'scheduling' && unassignedMealCount > 0 && (
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-900">
                💡 <strong>What would you like to do?</strong>
              </p>
              <ul className="mt-2 text-sm text-blue-800 space-y-1">
                <li>• Schedule your pending meals on the calendar</li>
                <li>• Start a new meal plan (old meals stay available for 7 days)</li>
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              fullWidth
              onClick={handleContinueExistingSession}
            >
              {existingMealSession.status === 'scheduling' ? '📅 Go to Calendar' : 'Continue Existing Session'}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              onClick={handleStartFreshSession}
            >
              {existingMealSession.status === 'scheduling' ? '🆕 Start Fresh' : 'Start Fresh'}
            </Button>
          </div>

          {!(existingMealSession.status === 'scheduling' && unassignedMealCount > 0) && (
            <p className="text-xs text-gray-400 mt-4 text-center">
              Status: {(existingMealSession.status === 'created' || existingMealSession.status === 'planning') && 'Planning'}
              {existingMealSession.status === 'shopping' && 'Shopping List'}
              {existingMealSession.status === 'scheduling' && 'Calendar'}
            </p>
          )}
        </Modal>
      )}

      {/* TodayPage Component */}
      <TodayPage
        onStartPlanning={(mealType, servings, forceWeekly) => {
          if (forceWeekly) {
            setForceWeeklyDefaults(true)
            clientLogger.debug({}, 'Forcing weekly defaults (5-day workweek)')
          }
          if (mealType && servings) {
            // Pre-select meal type and servings in session preferences
            // TODO: Implement pre-selection logic
            clientLogger.debug({ mealType, servings }, 'Quick plan selected')
          }
          handleStartPlanning()
        }}
        onShowAuth={handleShowAuth}
      />
    </>
  )
})

Home.displayName = 'Home'

export default Home